import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ToastService } from '@core/services/toast.service';
import { ApiService, type ApiServiceKey } from '@core/services/api/api.service';
import type {
  DataMapCollection,
  ParametersApiDataRow,
  ParametersColumnDef,
  ParametersCompanyContext,
  ParametersFormLineModel,
} from './kl-parameters.models';

function deepClone<T>(value: T): T {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

@Component({
  selector: 'kl-parameters',
  standalone: false,
  templateUrl: './kl-parameters.html',
  styleUrl: './kl-parameters.scss',
})
export class KlParametersComponent {
  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  readonly companyForm = this.fb.group({
    company: this.fb.control<ParametersCompanyContext | null>(null, Validators.required),
  });

  /** Optional; used for company-scoped application params. General settings can omit. */
  readonly type = input('');
  readonly endpoint = input('application-management-params');
  readonly filter = input<Record<string, unknown> | undefined>(undefined);
  readonly microApiService = input<string | undefined>(undefined);

  readonly defaultCollectionData = input<ParametersFormLineModel[] | undefined>(undefined);
  readonly collectionData = input<ParametersFormLineModel[]>([]);
  readonly dataMapCollection = input<DataMapCollection[]>([]);
  readonly columns = input<ParametersColumnDef[]>([]);

  readonly companyList = input<ParametersCompanyContext[]>([]);
  readonly currentCompany = model<ParametersCompanyContext | null>(null);

  readonly overrideFormSubmission = input(false);
  readonly modalAction = input<{ index: number; value: unknown } | null>(null);
  readonly exceptionInCommit = input<{ key_num: number; value: unknown } | undefined>(undefined);

  /** When false, dropdown captions from `getDescription` are shown as-is (e.g. timezone labels). */
  readonly translateMapCaptions = input(true);

  readonly openModalEvent = output<number>();
  readonly formSubmissionOverridden = output<ParametersFormLineModel[]>();

  readonly rows = signal<ParametersFormLineModel[]>([]);
  readonly loading = signal(false);
  readonly loadingText = signal('');
  readonly selectedRowIndex = signal<number | null>(null);

  readonly valueColumn = signal<ParametersColumnDef | undefined>(undefined);

  /** Table header labels from legacy `columns()` when present. */
  readonly descriptionHeaderKey = computed(
    () => this.columns().find((c) => c.binding === 'description')?.header ?? 'description'
  );
  readonly valueHeaderKey = computed(() => this.columns().find((c) => c.binding === 'value')?.header ?? 'value');
  /** Opt-in: `value1` column shows the row's `fileValue` (payload `value1`). */
  readonly value1Column = computed(() => this.columns().find((c) => c.binding === 'value1' && c.visible !== false));
  readonly value1HeaderKey = computed(() => this.value1Column()?.header ?? 'value1');
  readonly columnCount = computed(() => (this.value1Column() ? 3 : 2));

  private companyScopedInitialFetchDone = false;

  constructor() {
    effect(() => {
      const data = this.collectionData();
      // Same reference as parent when possible so in-place parent updates (legacy pattern) stay visible.
      untracked(() => {
        this.rows.set(data);
        // A filtered or reloaded list would make the old index point at a different row.
        this.selectedRowIndex.set(null);
      });
    });

    effect(() => {
      const cols = this.columns();
      untracked(() => this.valueColumn.set(cols.find((c) => c.binding === 'value' && c.visible !== false)));
    });

    effect(() => {
      const action = this.modalAction();
      if (!action) return;
      untracked(() => this.patchRowValue(action.index, action.value));
    });

    effect(() => {
      const cc = this.currentCompany();
      untracked(() => {
        const ctrl = this.companyForm.get('company');
        if (!ctrl || ctrl.value === cc) return;
        ctrl.setValue(cc, { emitEvent: false });
      });
    });

    /** When parent supplies `companyList` + `currentCompany`, load values once (legacy application params). */
    effect(() => {
      const list = this.companyList();
      const cc = this.currentCompany();
      if (!list.length || cc == null || cc.id == null) return;
      untracked(() => {
        if (this.companyScopedInitialFetchDone) return;
        this.companyScopedInitialFetchDone = true;
        queueMicrotask(() => this.changeCompany({ value: cc }));
      });
    });

    this.companyForm
      .get('company')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company) => this.changeCompany({ value: company }));
  }

  private apiKey(): ApiServiceKey {
    const micro = this.microApiService();
    if (micro === 'fuel' || micro === 'restaurant' || micro === 'schedule') return micro;
    return 'api';
  }

  onFormCommit(_action: string | null): void {
    this.commitSave();
  }

  changeCompany(ev: { value?: ParametersCompanyContext | null }): void {
    const company = ev?.value ?? null;
    this.currentCompany.set(company);
    if (company?.id == null) return;

    this.resetRowsFromTemplate();
    this.setLoading('loading');

    const filterBody = this.buildFilterForCompany(company);

    this.api
      .getFilteredData(this.endpoint(), filterBody, { service: this.apiKey() })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.clearLoading())
      )
      .subscribe({
        next: (response) => {
          const responseData = (response?.data as ParametersApiDataRow[]) ?? [];
          this.applyServerValues(responseData, company);
        },
        error: () => {},
      });
  }

  private buildFilterForCompany(company: ParametersCompanyContext): Record<string, unknown> {
    if (this.microApiService() === 'fuel') {
      const fuelFilterBody = deepClone(this.filter() ?? { filter: {} }) as Record<string, unknown>;
      const inner = fuelFilterBody['filter'] as Record<string, unknown> | undefined;
      if (inner) {
        const sr = inner['search_requests'];
        if (Array.isArray(sr) && sr[0]) {
          (sr[0] as Record<string, unknown>)['value'] = company.id;
        }
        inner['company_id'] = company.id;
      }
      return fuelFilterBody;
    }
    return (
      (this.filter() as Record<string, unknown>) ?? {
        filter: {
          company: { id: company.id },
          type: this.type(),
        },
      }
    );
  }

  private resetRowsFromTemplate(): void {
    const def = this.defaultCollectionData();
    const src = def?.length ? def : this.collectionData();
    this.rows.set(deepClone(src));
  }

  private applyServerValues(responseData: ParametersApiDataRow[], company: ParametersCompanyContext): void {
    const typeVal = this.type();
    const list = [...this.rows()];
    for (const row of list) {
      for (const data of responseData) {
        if (data.key_num === row.key_num) {
          row.value = data.value;
          if (data.id !== undefined) row.id = data.id;
        }
      }
      Object.assign(row, { company, type: typeVal });
    }
    this.rows.set(list);
  }

  commitSave(): void {
    if (!this.overrideFormSubmission()) {
      this.setLoading('saving');
    }

    const sendData: ParametersFormLineModel[] = [];
    const ex = this.exceptionInCommit();

    for (const line of this.rows()) {
      if (ex && line.key_num === ex.key_num) {
        sendData.push({
          key_num: ex.key_num,
          value: ex.value,
          type: this.type(),
        } as ParametersFormLineModel);
      } else if (line.key_num) {
        sendData.push(line);
      }
    }

    if (this.overrideFormSubmission()) {
      this.formSubmissionOverridden.emit(sendData);
      return;
    }

    this.api
      .addData(this.endpoint(), sendData as object, this.apiKey())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.clearLoading())
      )
      .subscribe({
        next: () => {
          this.toast.success({ summary: 'success_message', life: 3000 });
        },
        error: () => {},
      });
  }

  getDataMap(keyNum: number | null | undefined): DataMapCollection[] {
    if (keyNum == null) return [];
    return this.dataMapCollection().filter((d) => d.key_num === keyNum);
  }

  getDescription(cellItem: ParametersFormLineModel): string {
    for (const data of this.dataMapCollection()) {
      if (data.value === cellItem.value && data.key_num === cellItem.key_num) {
        return data.description;
      }
    }
    return '';
  }

  openSelectableModal(index: number): void {
    this.openModalEvent.emit(index);
  }

  onFileChosen(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const picked = input.files?.[0];
    const list = [...this.rows()];
    const row = list[index];
    if (!row) return;
    if (picked) {
      row.value = picked.name;
      row.fileValue = picked;
    } else {
      row.value = '';
      row.fileValue = undefined;
    }
    this.rows.set(list);
    input.value = '';
  }

  fileInputClick(index: number): void {
    document.getElementById(`kl-param-file-${index}`)?.click();
  }

  patchRowValue(index: number, value: unknown): void {
    const list = [...this.rows()];
    const row = list[index];
    if (!row) return;
    row.value = value;
    this.rows.set(list);
  }

  patchRowNumber(index: number, value: number | null): void {
    this.patchRowValue(index, value);
  }

  stringInputType(row: ParametersFormLineModel): string {
    if (row.inputType === 'password') return 'password';
    const t = row['inputType'];
    return t === 'string' || t === 'text' || t === 'input' ? 'text' : 'text';
  }

  toDateInputValue(v: unknown): string {
    if (v == null || v === '') return '';
    const d = new Date(v as string);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  }

  fromDateInputValue(s: string): string {
    return s;
  }

  asString(v: unknown): string {
    return v == null ? '' : String(v);
  }

  valueReadonly(): boolean {
    return !!this.valueColumn()?.isReadOnly;
  }

  isSectionRow(row: ParametersFormLineModel): boolean {
    return !!(row.header || row.inputType === 'header');
  }

  isStringType(row: ParametersFormLineModel): boolean {
    const t = row.inputType;
    return t === 'string' || t === 'text' || t === 'input' || t === undefined;
  }

  /** A freshly picked file is still a `File` until save, so only the stored string is shown. */
  value1Text(row: ParametersFormLineModel): string {
    return typeof row.fileValue === 'string' ? row.fileValue : '';
  }

  truncateMiddle(v: unknown, max = 100): string {
    const s = v == null ? '' : String(v);
    if (s.length <= max) return s;
    const half = Math.floor((max - 3) / 2);
    return s.slice(0, half) + '...' + s.slice(s.length - half);
  }

  private setLoading(key: string): void {
    this.loading.set(true);
    this.loadingText.set(key);
  }

  private clearLoading(): void {
    this.loading.set(false);
    this.loadingText.set('');
  }
}
